import React, { useState, useEffect } from 'react';
import { Modal, Box, Button, Form, Header, SpaceBetween, Link, Alert } from '@cloudscape-design/components';

import { InfoLink } from '../../commons/common-components';
import { submissionsApi } from '../../../api/Submission';
import DistributionsPanel from './distribution-panel';
import { useBreadcrumbs } from '../../../components/breadcrumbs/index';
import { useNavigate } from 'react-router-dom';


export function FormHeader({ loadHelpPanelContent }) {
  return (
    <Header
      variant="h1"
      info={<InfoLink id="form-main-info-link" onFollow={() => loadHelpPanelContent(0)} />}
      description="When you create an Amazon CloudFront distribution, you tell CloudFront where to find your content by specifying your origin servers."
    >
      Submit the Indicator Evidences 
    </Header>
  );
}


const AIConfirmationModal = ({ 
  visible, 
  onDismiss, 
  onConfirm,
  isProcessing
}) => {
  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header="AI Review Confirmation"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button 
              variant="link" 
              onClick={onDismiss}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={onConfirm}
              loading={isProcessing}
            >
              Confirm
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <p>
        Are you sure you want to get AI assessments?
      </p>
    </Modal>
  );
};


function FormActions({ onCancelClick, onSubmitClick, onRecommendationHintsClick, isLoading, isGenAILoading }) {
  const [showAIConfirmation, setShowAIConfirmation] = useState(false);

  const handleSaveAndGenAI = () => {
    setShowAIConfirmation(true);
  };

  const handleAIConfirm = async () => {
    await onRecommendationHintsClick();
    setShowAIConfirmation(false);
  };

  return (
    <>
      <SpaceBetween direction="horizontal" size="xs">
        <Button variant='normal' onClick={onCancelClick}>
          Cancel
        </Button>
        <Button 
        variant='normal'
          data-testid="create-genai" 
          onClick={handleSaveAndGenAI}
        >
          Save & GenAI Review
        </Button>
        <Button 
          data-testid="create" 
          variant="primary"
          loading={isLoading}
          onClick={onSubmitClick}
        >
          Save
        </Button>
      </SpaceBetween>

      <AIConfirmationModal
        visible={showAIConfirmation}
        onDismiss={() => setShowAIConfirmation(false)}
        onConfirm={handleAIConfirm}
        isProcessing={isGenAILoading}
      />
    </>
  );
}
function BaseForm({ 
  content, 
  onCancelClick, 
  errorText = null, 
  onSubmitClick, 
  header, 
  onRecommendationHintsClick, 
  isLoading, 
  isGenAILoading,
  aiError = null ,
  submissionId
}) {
  const navigate = useNavigate();
  return (
    <form
      onSubmit={event => {
        event.preventDefault();
      }}
    >
      <SpaceBetween size="m">
        {/* {aiError && (
          <Alert type="error">
            {aiError}
          </Alert>
        )} */}
        <Form
          header={header}
          actions={
            <FormActions 
              onCancelClick={() => navigate(`/application-details/${submissionId}`)} 
              onSubmitClick={onSubmitClick} 
              onRecommendationHintsClick={onRecommendationHintsClick}
              isLoading={isLoading}
              isGenAILoading={isGenAILoading}
            />
          }
          errorText={aiError}
          errorIconAriaLabel="Error"
        >
          {content}
        </Form>
      </SpaceBetween>
    </form>
  );
}

const defaultData = {
  sslCertificate: 'default',
  cloudFrontRootObject: '',
  alternativeDomainNames: '',
  s3BucketSelectedOption: null,
  certificateExpiryDate: '',
  certificateExpiryTime: '',
  httpVersion: 'http2',
  ipv6isOn: false,
  functions: [],
  evidences: [],
  originId: '',
  customHeaders: [{}],
  codeEditor: '',
};

export function FormFull({ loadHelpPanelContent, header, indicatorSubmissionID }) {
  const [isLoading, setIsLoading] = useState(false);
  const [isGenAILoading, setIsGenAILoading] = useState(false);
  const [data, _setData] = useState(defaultData);
  const [aiError, setAIError] = useState(null);

  useEffect(() => {
    const fetchIndicatorSubmission = async () => { 
      const indicatorSubmissionData = await submissionsApi.getIndicatorSubmission(indicatorSubmissionID)
      indicatorSubmissionData.evidences = indicatorSubmissionData.files.map((file) => ({
        fileID: file.id,
        file: new File([new Blob([])], file.s3_key, 
        {
          type: 'application/pdf',
          created: file.created
        })}));
      _setData(data => ({...data, ...indicatorSubmissionData}));
      setActiveBreadcrumb(
        { text: `${indicatorSubmissionData.indicator.index} ${indicatorSubmissionData.indicator.name}`, href: '#', index: 4 },
        4
      );
  
    }
    fetchIndicatorSubmission();
  }, [indicatorSubmissionID]);

  const submitIndicatorForm = async () => {
    setIsLoading(true);
    setAIError(null);
    try {
      await submissionsApi.updateIndicatorText(indicatorSubmissionID, data.text);
    } catch (error) {
      console.error('Failed to submit form:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const onRecommendationHintsClick = async () => {
    setIsGenAILoading(true);
    setAIError(null);
    try {
      if (!data.text){
        setAIError('Please enter some text before requesting an AI review.');
        return;
      }
      await submissionsApi.updateIndicatorText(indicatorSubmissionID, data.text);
      await submissionsApi.invokeAiCheck(indicatorSubmissionID);

    } catch (error) {
      if (error.response?.data?.detail) {
        const { error_code, message } = error.response.data.detail;
        switch (error_code) {
          case 1:
            setAIError('Please enter some text before requesting an AI review.');
            break;
          case 2:
            setAIError('Please attach at least one file before requesting an AI review.');
            break;
          case 3:
            setAIError('Another AI invocation is processing for this indicator');
            break;
          default:
            setAIError('An error occurred while processing your AI review request. Please try again.');
        }
      } else {
        setAIError('An unexpected error occurred. Please try again later.');
      }
    } finally {
      setIsGenAILoading(false);
    }
  };

  const setData = (updateObj = {}) => _setData(prevData => ({ ...prevData, ...updateObj }));

  const { setActiveBreadcrumb } = useBreadcrumbs();

  return (
    <BaseForm
      header={header}
      submissionId={data.submission_id}
      content={
        <SpaceBetween size="l">
          <DistributionsPanel  data={data} setData={setData} setErrors={setAIError}/>
        </SpaceBetween>
      }
      onSubmitClick={submitIndicatorForm}
      onRecommendationHintsClick={onRecommendationHintsClick}
      isLoading={isLoading}
      isGenAILoading={isGenAILoading}
      aiError={aiError}
    />
  );
}